import { FormattedMessage } from 'react-intl';

import { CapacityWarning } from '#components/tour/CreateTourDialog/CapacityWarning.tsx';
import { LoadMetricsWarningIcon } from '#components/tour/CreateTourDialog/LoadMetricsWarningIcon.tsx';
import { Metric } from '#components/tour/CreateTourDialog/Metric.tsx';
import type { StopType } from '#domain/Stop.ts';

import type { MissingCargoData, StopLoadMetrics } from './loadMetrics.ts';

type LoadMetricsProps = {
    metrics?: StopLoadMetrics;
    stopType: StopType;
};

const getMissingCargoData = (metrics: StopLoadMetrics): MissingCargoData => {
    if (metrics.missingCargoData) {
        return metrics.missingCargoData;
    }
    if (!metrics.weight && !metrics.loadingMeter && (metrics.liter || metrics.cubicMeter)) {
        return 'quantity';
    }
    if (metrics.weight && !metrics.loadingMeter) {
        return 'weight';
    }
    if (metrics.loadingMeter && !metrics.weight) {
        return 'loadingMeter';
    }
    return 'weightAndLoadingMeter';
};

export const LoadMetrics = ({ metrics, stopType }: LoadMetricsProps) => {
    if (!metrics) {
        return null;
    }

    const metricItems = [
        {
            capacityMetric: 'weight' as const,
            label: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.weight',
            metric: metrics.weight,
            unit: 'kg',
        },
        {
            capacityMetric: 'loadingMeter' as const,
            label: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.loading-meter',
            metric: metrics.loadingMeter,
            unit: 'Ldm',
        },
        {
            capacityMetric: 'liter' as const,
            label: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.quantity',
            metric: metrics.liter,
            unit: 'l',
        },
        {
            capacityMetric: 'cubicMeter' as const,
            label: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.quantity',
            metric: metrics.cubicMeter,
            unit: 'm³',
        },
    ].flatMap(item => (item.metric ? [{ ...item, metric: item.metric }] : []));
    const hasMetrics = metricItems.length > 0;
    const missingCargoData = getMissingCargoData(metrics);
    const showWarning = metrics.valueState === 'partial' || (metrics.valueState === 'missing' && hasMetrics);
    const exceededCapacity = metrics.exceededCapacity ?? [];
    const showCapacityWarning = exceededCapacity.length > 0;

    if (!hasMetrics && metrics.valueState === 'complete') {
        return null;
    }

    return (
        <div
            className='display-flex flex-wrap align-items-center gap-15 margin-top-10 rounded padding-10 bg-lightest'
            data-testid='load-metrics'
        >
            <div className='display-flex align-items-center gap-5 flex-0' data-testid='load-metrics-heading'>
                <span
                    className='rioglyph rioglyph-truck text-size-16 text-color-darker display-flex align-items-center justify-content-center'
                    aria-hidden='true'
                />
                <span className='text-size-12 text-medium'>
                    <FormattedMessage id='intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.title' />
                </span>
                {showWarning && <LoadMetricsWarningIcon missingData={missingCargoData} />}
                {showCapacityWarning && <CapacityWarning dimensions={exceededCapacity} />}
            </div>
            {hasMetrics && (
                <div
                    className='display-flex flex-wrap align-items-start justify-content-end gap-15 flex-1-1-0 min-width-0'
                    data-testid='load-metrics-values'
                >
                    {metricItems.map(({ capacityMetric, label, metric, unit }) => (
                        <Metric
                            key={unit}
                            exceeded={exceededCapacity.includes(capacityMetric)}
                            label={label}
                            metric={metric}
                            stopType={stopType}
                            unit={unit}
                        />
                    ))}
                </div>
            )}
            {!hasMetrics && metrics.valueState === 'missing' && (
                <span
                    className='flex-1-1-0 text-right text-size-12 text-color-dark'
                    data-testid='load-metrics-value-state-message'
                >
                    <FormattedMessage id='intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.no-values' />
                </span>
            )}
        </div>
    );
};
